import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { AppUI, AfterAuth } from '@seedlang/state';
import { AuthenticateStore, ServerStore } from '@seedlang/stores';
import FormWrapper from 'components/authenticate/form_wrapper';
import Button from 'components/button/button';
import Spinner from 'components/spinner';
import Text from 'components/text';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import Link from 'components/link';
import PatreonButton from 'components/user/patreon_button';
import Stack from 'components/layout/stack';

const Alert = styled.div`
  font-size: 16px;
  line-height: 18px;
  color: #333;
  margin: 20px 0;
  text-align: center;
  color: ${props => props.color};
  text-wrap: balance;
`;

const GoHomeButton = styled(Link)`
  font-size: 14px;
  color: #000;
  text-decoration: underline;
`;

const ConfirmFormWrapper = styled(FormWrapper)`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const ConfirmPanel = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 100%;
  width: 100%;
`;

const ConfirmPanelContent = styled.div`
  width: 100%;
`;

const ContactLinkFooter = styled.div`
  font-size: 14px;
  margin-top: auto;
  padding-top: 20px;
  text-align: center;
  width: 100%;
`;

const ContactLink = styled(Link)`
  color: #333;
  text-decoration: underline;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  text-decoration: underline;
  width: auto !important;
  padding: 0;
  display: inline;
`;

const Title = ({ children, siteIsSeedlang, textColor }) => (
  <Text
    center
    heading='3'
    color={textColor || (siteIsSeedlang ? '#FFF' : '#333')}
  >
    {children}
  </Text>
);

export const AuthenticateConfirm = ({
  accentColor,
  background,
  borderRadius,
  confirmationTitle,
  errorMessage,
  loading,
  mode,
  nextStep,
  onContinueClick,
  onResendClick,
  patreonButton,
  showContactLink,
  showPatreonConnect,
  signIn,
  siteIsSeedlang,
  textColor,
  width,
}) => (
  <ConfirmFormWrapper
    padding={0}
    background={background}
    borderRadius={borderRadius}
    width={width}
  >
    <ConfirmPanel>
      <ConfirmPanelContent>
        {
          loading ? (
            <>
              <Title siteIsSeedlang={siteIsSeedlang} textColor={textColor}>Email Confirmation</Title>
              <Spinner background={accentColor || Theme.blue} />
            </>
          ) :
            errorMessage ? (
              <>
                <Title siteIsSeedlang={siteIsSeedlang} textColor={textColor}>Email Confirmation</Title>
                <Alert color={textColor || (siteIsSeedlang ? '#FFF' : '#333')}>
                  {
                    errorMessage === 'Email was already confirmed, please try signing in'
                      ? <>Email was already confirmed, please try <LinkButton onClick={signIn}>signing in</LinkButton>.</>
                      : errorMessage
                  }
                </Alert>
              </>
            ) :
              mode === 'confirmationRequired' ? (
                <>
                  <Title siteIsSeedlang={siteIsSeedlang} textColor={textColor}>Please confirm your email address</Title>
                  <Alert color={textColor || (siteIsSeedlang ? '#FFF' : '#333')}>
                    <p>
                      Please click the link in the email we have sent you.
                    </p>
                    <p>
                      Allow a few minutes for the email to arrive and check your Spam folder.
                    </p>
                    <Button
                      height='50px'
                      width='100%'
                      margin='20px 0 0 0'
                      borderRadius='50px'
                      background={accentColor}
                      backgroundOnHover={accentColor}
                      color='#FFF'
                      onClick={onResendClick}
                    >
                      Resend email
                    </Button>
                  </Alert>
                </>
              ) :
                mode === 'emailResent' ? (
                  <>
                    <Title siteIsSeedlang={siteIsSeedlang} textColor={textColor}>Please confirm your email address</Title>
                    <Alert background={Theme.green} padding={10} marginTop={10}>
                      <p>
                        The confirmation email has been resent.
                      </p>
                      <p>
                        Allow a few minutes for the email to arrive and check your Spam folder.
                      </p>
                    </Alert>
                  </>
                ) :
                  mode === 'confirmed' ? (
                    <>
                      {confirmationTitle && <Title siteIsSeedlang={siteIsSeedlang} textColor={textColor}>{confirmationTitle}</Title>}
                      <Alert>
                        {
                          showPatreonConnect && (
                            <Stack gap='md' alignItems='center'>
                              <p>
                                Do you have a paid Patreon membership? Connect it now to access your benefits.
                              </p>
                              {patreonButton}
                              <GoHomeButton to={nextStep.path}>Go Home</GoHomeButton>
                            </Stack>
                          )
                        }
                        {
                          !showPatreonConnect && nextStep?.path &&
                            <Link to={nextStep.path}>
                              <Button
                                height='50px'
                                width='100%'
                                borderRadius='50px'
                                background={accentColor}
                                backgroundOnHover={accentColor}
                                color='#FFF'
                                onClick={onContinueClick}
                              >
                                {nextStep.label ?? 'Continue'}
                              </Button>
                            </Link>
                        }
                      </Alert>
                    </>
                  ) : (
                    <>
                      <Title siteIsSeedlang={siteIsSeedlang} textColor={textColor}>Email Confirmation</Title>
                      <Alert color={textColor || (siteIsSeedlang ? '#FFF' : '#333')}>
                        Something went wrong. Please try again.
                      </Alert>
                    </>
                  )
        }
      </ConfirmPanelContent>
      {
        showContactLink && !siteIsSeedlang && (
          <ContactLinkFooter>
            <ContactLink to='/contact'>Contact Us</ContactLink>
          </ContactLinkFooter>
        )
      }
    </ConfirmPanel>
  </ConfirmFormWrapper>
);

@observer
class AuthenticateConfirmWrapper extends React.Component {
  @observable loading = false;
  @observable emailResent = false;
  @observable errorMessage = null;
  @observable mode = 'confirmationRequired';

  componentDidMount() {
    ServerStore.clearMessages();

    if (this.tokenFromUrl) {
      this.validateToken();
    }
  }

  get tokenFromUrl() {
    return new URLSearchParams(window.location.search).get('token');
  }
  onResendClick = async () => {
    try {
      this.errorMessage = null;
      this.loading = true;
      this.mode = 'resendingEmail';

      await AuthenticateStore.sendConfirmationInstructions();
      this.mode = 'emailResent';
    }
    catch (error) {
      console.error(error);
      this.errorMessage = error.message ?? 'Sorry, there was an error sending the confirmation email.';
    }
    finally {
      this.loading = false;
    }
  }
  validateToken = async () => {
    try {
      this.errorMessage = null;
      this.loading = true;
      this.mode = 'validatingToken';

      await AuthenticateStore.validateEmailConfirmationToken({ data: { token: this.tokenFromUrl } });

      // Safari seems to be doing some caching, when requesting a /api/auth_user,
      // any console.log statements added to the ajax call show that it is being executed,
      // but the request does not show up in Safari's network tab, nor does it trigger the Rails app.
      await AppUI.loadUserPromise({ queryStrings: { cache_buster: Math.round(Math.random() * 10 ** 12) } });

      if (!AppUI.user || AppUI.user.signedOut || !AppUI.user.id) {
        throw new Error('Sorry, an application error occurred. Your email address has been confirmed, but the server did not return user details. Please try signing in on the home page.');
      }

      this.mode = 'confirmed';
      this.props.onConfirmed?.();
    }
    catch (error) {
      console.error(error);
      this.errorMessage = error.message ?? 'Sorry, there was an error validating your email address.';
    }
    finally {
      this.loading = false;
    }
  }
  onContinueClick = () => {
    AfterAuth.clear();
  }

  get nextStep() {
    if (AppUI.siteIsSeedlang) {
      return {
        path: AppUI.userIsSignedInAndNotGuest ? 'reviews.home' : '/onboarding/sign_in',
        label: AppUI.userIsSignedInAndNotGuest ? 'Start Learning' : 'Sign in',
      };
    }

    if (AfterAuth.value) return AfterAuth.value;

    if (AppUI.user?.enterNewPassword) {
      return {
        path: '/onboarding/create_password',
      };
    }

    const homePageRoute = AppUI.userIsSignedInAndNotGuest
      ? AppUI.site.signedInHomePageRoute
      : AppUI.site.signedOutHomePageRoute;

    return {
      path: homePageRoute,
      label: 'Go Home',
    };
  }

  @computed get confirmationTitle() {
    return AppUI.siteIsSeedlang ? null : 'Email confirmed';
  }

  @computed get patreonConnectAvailable() {
    return AppUI.site.patreonClientId && !AppUI.authUserStore.showData.patreonUser;
  }

  @computed get showPatreonConnect() {
    if (!this.patreonConnectAvailable) return false;

    const shouldShow = localStorage.getItem('showPatreonConnectOnConfirm') === 'true';
    if (shouldShow) localStorage.removeItem('showPatreonConnectOnConfirm');
    return shouldShow;
  }

  @computed get showContactLink() {
    return ['confirmationRequired', 'emailResent'].includes(this.mode);
  }

  render() {
    const isConfirmed = this.mode === 'confirmed';

    return (
      <AuthenticateConfirm
        accentColor={AppUI.site.accentColor}
        background={this.props.background}
        borderRadius={this.props.borderRadius}
        confirmationTitle={isConfirmed ? this.confirmationTitle : null}
        errorMessage={this.errorMessage}
        loading={this.loading}
        mode={this.mode}
        nextStep={this.nextStep}
        onContinueClick={this.onContinueClick}
        onResendClick={this.onResendClick}
        patreonButton={<PatreonButton />}
        showContactLink={this.showContactLink}
        showPatreonConnect={isConfirmed ? this.showPatreonConnect : false}
        signIn={this.props.signIn}
        siteIsSeedlang={AppUI.siteIsSeedlang}
        textColor={this.props.textColor}
        width={this.props.width}
      />
    );
  }
}

export default AuthenticateConfirmWrapper;
